import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import type { ClaimState } from "../store/VnvAssistStore";

export interface VnvAssistDialogueData {
  vnv_assist_run_id: number | null;
  input_artifact_id: number | null;
  dataset_ids: number[];
}

export interface UpdateVnvAssistDialogueData {
  input_artifact_id: number | null;
  dataset_ids: number[];
}

export interface CreateVnvAssistRunPayload {
  dialogue_id?: number;
  input_artifact_id?: number;
  dataset_ids?: number[];
}

// ---------------------------------------------------------------------------
// Create run
// ---------------------------------------------------------------------------

const createVnvAssistRun = async (
  payload: CreateVnvAssistRunPayload,
): Promise<number> => {
  const response = await axios.post(
    `${nuclearn_api_url}/products/vnv-assist/create-run`,
    payload,
  );
  return response.data;
};

export const useCreateVnvAssistRun = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: CreateVnvAssistRunPayload) => createVnvAssistRun(payload),
    {
      onSuccess: () => queryClient.invalidateQueries(["vnvAssistRun"]),
    },
  );
};

// ---------------------------------------------------------------------------
// Dialogue data
// ---------------------------------------------------------------------------

export const getVnvAssistDialogueData = async (
  dialogueId: number,
): Promise<VnvAssistDialogueData> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/vnv-assist/dialogue/${dialogueId}/data`,
  );
  return response.data;
};

export const useGetVnvAssistDialogueData = (dialogueId: number | undefined) => {
  return useQuery(
    ["vnvAssistRun", dialogueId],
    () => getVnvAssistDialogueData(dialogueId!),
    {
      enabled: !!dialogueId,
      refetchOnWindowFocus: false,
    },
  );
};

const updateVnvAssistDialogueData = async (
  dialogueId: number,
  payload: UpdateVnvAssistDialogueData,
): Promise<VnvAssistDialogueData> => {
  const response = await axios.put(
    `${nuclearn_api_url}/products/vnv-assist/dialogue/${dialogueId}/data`,
    payload,
  );
  return response.data;
};

export const useUpdateVnvAssistDialogueData = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      dialogueId,
      payload,
    }: {
      dialogueId: number;
      payload: UpdateVnvAssistDialogueData;
    }) => updateVnvAssistDialogueData(dialogueId, payload),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(["vnvAssistRun", variables.dialogueId]);
      },
    },
  );
};

// ---------------------------------------------------------------------------
// Claims
// ---------------------------------------------------------------------------

export const getVnvAssistClaims = async (
  runId: number,
): Promise<ClaimState[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/products/vnv-assist/run/${runId}/claims`,
  );
  return response.data;
};

const deleteVnvAssistClaim = async (
  runId: number,
  claimId: number,
): Promise<void> => {
  await axios.delete(
    `${nuclearn_api_url}/products/vnv-assist/run/${runId}/claims/${claimId}`,
  );
};

export const useDeleteVnvAssistClaim = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ runId, claimId }: { runId: number; claimId: number }) =>
      deleteVnvAssistClaim(runId, claimId),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(["vnvAssistClaims", variables.runId]);
      },
    },
  );
};

// ---------------------------------------------------------------------------
// Run listings (recents page)
// ---------------------------------------------------------------------------

/**
 * Mirrors backend ``RunStatus`` (mre/products/vnv_assist/datamodels.py).
 * Hand-maintained — there is no FE codegen pipeline.
 */
export type VnvAssistRunStatus =
  | "pending"
  | "identifying"
  | "identified"
  | "failed";

export interface VnvAssistRunSummary {
  id: number;
  dialogue_id: number;
  input_artifact_id: number | null;
  input_artifact_filename: string | null;
  status: VnvAssistRunStatus;
  claim_count: number;
  create_date: string;
}

/** Paginated envelope returned by ``GET /products/vnv-assist/runs``. */
export interface VnvAssistRunsPage {
  runs: VnvAssistRunSummary[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface UseGetVnvAssistRunsOptions {
  /**
   * react-query ``staleTime`` (ms). Data is considered fresh for this
   * window, so background refetch triggers (window focus, mount) are
   * suppressed until it elapses — explicit ``invalidateQueries`` calls
   * still refetch immediately. Defaults to 30s so a splash widget that
   * gets re-mounted as the user navigates around doesn't re-hit the
   * endpoint each time. The recents page can pass a shorter value if
   * intra-session freshness matters there.
   */
  staleTime?: number;
  /**
   * react-query ``refetchOnWindowFocus``. Defaults to ``false`` to avoid
   * a refetch every time the user tab-switches back to the app — noisy on
   * the splash widget. Pass ``true`` from the recents page if a fresh
   * view on focus is worth the extra request.
   */
  refetchOnWindowFocus?: boolean;
}

/**
 * Fetch the current user's recent VNV Assist runs.
 */
export const useGetVnvAssistRuns = (
  limit = 50,
  options: UseGetVnvAssistRunsOptions = {},
) => {
  const { staleTime = 30_000, refetchOnWindowFocus = false } = options;
  return useQuery(
    ["vnvAssistRuns", limit],
    async () => {
      const res = await axios.get<VnvAssistRunsPage>(
        `${nuclearn_api_url}/products/vnv-assist/runs`,
        { params: { limit } },
      );
      return res.data;
    },
    { staleTime, refetchOnWindowFocus },
  );
};
